<script lang="ts">
	import AdditionUserInfoBanner from './addition-user-info-banner.svelte';
	import LoginPage from '../../../routes/(auth)/login/+page.svelte';
	import ShallowRouting from '../general/shallow-routing.svelte';
	import ArrowUpRight from '@lucide/svelte/icons/arrow-up-right';
	import type { SelectUser, SelectUserDetail } from '$db/tables';
	import ShoppingCart from '@lucide/svelte/icons/shopping-cart';
	import VerifyEmailBanner from './verify-email-banner.svelte';
	import UserSearchDialog from './user-search-dialog.svelte';
	import * as Sheet from '$lib/components/ui/sheet';
	import UserDropdown from './user-dropdown.svelte';
	import { afterNavigate } from '$app/navigation';
	import Menu from '@lucide/svelte/icons/menu';
	import { Button } from '../ui/button';
	import { Badge } from '../ui/badge';
	import { page } from '$app/state';

	interface Props {
		user: (SelectUser & { userDetails: SelectUserDetail | null }) | undefined;
	}

	let { user }: Props = $props();
	let shallowRouting: {
		showModal: (event?: MouseEvent, link?: string) => Promise<void>;
	};
	let mobileOpen: boolean = $state(false);

	afterNavigate(() => (mobileOpen = false));

	let navActive = $derived((href: string) =>
		page.url.pathname.startsWith(href) ? 'underline underline-offset-2 decoration-2' : ''
	);
</script>

<ShallowRouting
	startUrl={page.url.pathname + page.url.search}
	component={LoginPage}
	href="/login"
	type="dialog"
	bind:this={shallowRouting}
/>

<header class="sticky top-0 z-50 h-fit bg-background drop-shadow-sm">
	<VerifyEmailBanner {user} />
	<AdditionUserInfoBanner {user} />
	<div class="container flex items-center justify-between py-5 md:flex-row flex-col gap-x-6 gap-y-4">
		<div class="flex items-center">
			<a href="/" class="text-xl font-bold text-primary md:pr-8"><img src="/logo.svg" alt="ASGE Logo" class="w-56" /></a>
			<nav class="hidden md:flex">
				<ul class="flex items-center gap-6 px-4">
					<li>
						<a
							href="/ausbildungen"
							class="{navActive('/ausbildungen')} font-semibold hover:underline">Ausbildungen</a
						>
					</li>
					<li>
						<a href="/standorte" class="{navActive('/standorte')} font-semibold hover:underline"
							>Standorte</a
						>
					</li>
					<li>
						<a href="/partner" class="{navActive('/partner')} font-semibold hover:underline"
							>Partner</a
						>
					</li>
					<li>
						<a href="/team" class="{navActive('/team')} font-semibold hover:underline">Team</a>
					</li>
					<!-- <li>
						<a href="/blog" class="{navActive('/blog')} font-semibold hover:underline">Blog</a>
					</li> -->
				</ul>
			</nav>
		</div>
		<div class="flex items-center gap-2">
			<Button href="/kontakt" variant="secondary" class="hidden md:block">Kontakt</Button>
			<UserSearchDialog />
			<Button href="/warenkorb" size="icon" variant="secondary" class="relative">
				<ShoppingCart class="size-5" /><span class="sr-only">Warenkorb</span>
				{#if page.data.cart && page.data.cart.cartItems[0]}
					<Badge
						variant="customer"
						class="absolute flex size-4 -translate-y-2 translate-x-2 justify-center px-1"
						>{page.data.cart.cartItems.length}</Badge
					>
				{/if}
			</Button>
			{#if !page.data.session}
				<div>
					<Button onclick={(e) => shallowRouting.showModal(e)} variant="secondary">Login</Button>
				</div>
			{:else if user?.userDetails}
				<UserDropdown {user} />
			{/if}
			<Sheet.Root bind:open={mobileOpen}>
				<Sheet.Trigger>
					{#snippet child({ props })}
						<Button {...props} variant="secondary" size="icon" class="md:hidden"
							><Menu class="size-5" /><span class="sr-only">Menü</span></Button
						>
					{/snippet}
				</Sheet.Trigger>
				<Sheet.Content side="right" class="flex flex-1 flex-col">
					<nav>
						<ul class="flex flex-col">
							<li>
								<Button variant="link" href="/ausbildungen" class="w-full justify-start"
									>Ausbildungen</Button
								>
							</li>
							<li>
								<Button variant="link" href="/partner" class="w-full justify-start">Partner</Button>
							</li>
							<li>
								<Button variant="link" href="/standorte" class="w-full justify-start"
									>Standorte</Button
								>
							</li>
							<li>
								<Button variant="link" href="/team" class="w-full justify-start">Team</Button>
							</li>
							{#if page.data.user?.roles?.includes('admin')}
								<li>
									<Button
										variant="link"
										href="/admin"
										class="group flex w-full items-center justify-between gap-1"
									>
										Adminseite <ArrowUpRight
											class="size-4 text-muted-foreground transition-all ease-in-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
										/></Button
									>
								</li>
							{/if}
							{#if page.data.user?.roles?.includes('teacher') && !page.url.pathname.includes('/referenten')}
								<li>
									<Button
										variant="link"
										href="/referenten/uebersicht"
										class="group flex w-full items-center justify-between gap-1"
									>
										Referentenbereich <ArrowUpRight
											class="size-4 text-muted-foreground transition-all ease-in-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
										/></Button
									>
								</li>
							{/if}
						</ul>
					</nav>
					<Button href="/kontakt" variant="secondary" class="mt-auto">Kontakt</Button>
				</Sheet.Content>
			</Sheet.Root>
		</div>
	</div>
</header>
